class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close()
		);
		this.querySelector("#CartDrawer-Overlay").addEventListener(
			"click",
			this.close.bind(this)
		);
		this.setHeaderCartIconAccessibility();
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");

		cartLink.addEventListener("click", (event) => {
			event.preventDefault();
			this.open(cartLink);
		});

		cartLink.addEventListener("keydown", (event) => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				this.open(cartLink);
			}
		});
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);

		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add("animate", "active");
		});

		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
			},
			{ once: true }
		);

		document.body.classList.add("cart-overflow-hidden");

		if (!isSafari()) {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.add("overflow-clip");
		}
	}

	close() {
		this.classList.remove("active");
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("cart-overflow-hidden");

		if (!isSafari()) {
			document.body.classList.remove("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-clip");
		}

		setTimeout(() => {
			let notification = this.querySelector(".notification");

			if (notification) {
				const activeItem = this.querySelector(
					`.cart-item[data-product-id="${this.productId}"`
				);
				notification.classList.remove("notification-open");
				activeItem?.classList.remove("active");
			}
		}, 1000);
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.querySelector(".drawer__inner").classList.contains("is-empty") &&
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		this.productId = parsedState.id;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			sectionElement.innerHTML = this.getSectionInnerHTML(
				parsedState.sections[section.id],
				section.selector
			);
		});

		let notification = this.querySelector(".notification");

		if (notification) {
			const activeItem = this.querySelector(
				`.cart-item[data-product-id="${this.productId}"`
			);
			notification.classList.add("notification-open");
			activeItem.classList.add("active");
		}

		setTimeout(() => {
			this.querySelector("#CartDrawer-Overlay").addEventListener(
				"click",
				this.close.bind(this)
			);
			this.open();
		});
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);

if (!customElements.get("cart-note")) {
	customElements.define(
		"cart-note",
		class CartNote extends HTMLElement {
			constructor() {
				super();

				// Drawer 版：显式 Save/Clear + toast；Cart 页版：输入即自动保存
				if (this.querySelector("[data-note-save]")) {
					this.textarea = this.querySelector("textarea");
					this.saveButton = this.querySelector("[data-note-save]");
					this.saveButtonLabel = this.querySelector("[data-note-save-label]");
					this.clearButton = this.querySelector("[data-note-clear]");
					this.counter = this.querySelector("[data-note-count]");
					const drawerInner = this.closest(".drawer__inner");
					this.toast = drawerInner
						? drawerInner.querySelector("[data-note-toast]")
						: null;
					this.toastTimer = null;
					this.defaultSaveLabel = this.saveButtonLabel
						? this.saveButtonLabel.textContent
						: "";

					this.updateCounter();

					this.textarea.addEventListener("input", () => this.updateCounter());
					this.saveButton.addEventListener("click", () => this.saveNote());
					this.clearButton.addEventListener("click", () => {
						this.textarea.value = "";
						this.updateCounter();
						this.saveNote();
					});
					return;
				}

				this.addEventListener(
					"input",
					debounce((event) => {
						const body = JSON.stringify({ note: event.target.value });
						fetch(`${routes.cart_update_url}`, {
							...fetchConfig(),
							...{ body },
						});
					}, ON_CHANGE_DEBOUNCE_TIMER)
				);
			}

			updateCounter() {
				if (!this.counter || !this.textarea) return;
				const max = this.textarea.getAttribute("maxlength") || 500;
				this.counter.textContent = `${this.textarea.value.length}/${max}`;
			}

			setSavingState(isSaving) {
				if (!this.saveButton) return;
				this.saveButton.disabled = isSaving;
				this.saveButton.classList.toggle("is-saving", isSaving);
				if (this.saveButtonLabel) {
					this.saveButtonLabel.textContent = isSaving
						? this.dataset.noteSaving
						: this.defaultSaveLabel;
				}
			}

			showToast(message) {
				if (!this.toast) return;
				clearTimeout(this.toastTimer);
				this.toast.textContent = message;
				this.toast.classList.add("is-visible");
				this.toastTimer = setTimeout(() => {
					this.toast.classList.remove("is-visible");
				}, 2500);
			}

			saveNote() {
				this.setSavingState(true);

				const body = JSON.stringify({ note: this.textarea.value });
				fetch(`${routes.cart_update_url}`, {
					...fetchConfig(),
					...{ body },
				})
					.then((response) => {
						this.setSavingState(false);
						if (response.ok) {
							const details = this.closest("details");
							if (details) details.removeAttribute("open");
							this.showToast(this.dataset.noteSaved);
						} else {
							this.showToast(this.dataset.noteSaveFailed);
						}
					})
					.catch(() => {
						this.setSavingState(false);
						this.showToast(this.dataset.noteSaveFailed);
					});
			}
		}
	);
}

function showCartToast(message) {
	const toast =
		document.querySelector(".main-cart [data-cart-toast]") ||
		document.querySelector("#CartDrawer [data-cart-toast]");
	if (!toast) return;
	clearTimeout(showCartToast.timer);
	toast.textContent = message;
	toast.classList.add("is-visible");
	showCartToast.timer = setTimeout(() => {
		toast.classList.remove("is-visible");
	}, 3000);
}

function renderCartDrawerSections(parsedState) {
	if (!parsedState.sections) return;
	const config = [
		{ id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
		{ id: "cart-icon-bubble", section: "cart-icon-bubble", selector: ".shopify-section" },
	];
	const cartItemsEl = document.getElementById("main-cart-items");
	if (cartItemsEl) {
		config.push({
			id: "main-cart-items",
			section: cartItemsEl.dataset.id || "main-cart-items",
			selector: ".js-contents",
		});
	}
	const cartFooterEl = document.getElementById("main-cart-footer");
	if (cartFooterEl) {
		config.push({
			id: "main-cart-footer",
			section: cartFooterEl.dataset.id || "main-cart-footer",
			selector: ".js-contents-totals",
		});
	}
	config.forEach((item) => {
		const html = parsedState.sections[item.section];
		const element = document.getElementById(item.id);
		if (!html || !element) return;
		const source = new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(item.selector);
		const target = element.querySelector(item.selector) || element;
		if (source && target) target.innerHTML = source.innerHTML;
	});
}

function escapeHtml(str) {
	return String(str)
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

if (!customElements.get("cart-item-edit")) {
	customElements.define(
		"cart-item-edit",
		class CartItemEdit extends HTMLElement {
			constructor() {
				super();
				this.optionsContainer = this.querySelector("[data-edit-options]");
				this.qtyValue = this.querySelector("[data-qty-value]");
				this.saveButton = this.querySelector("[data-edit-save]");
				this.saveLabel = this.querySelector("[data-edit-save-label]");
				this.data = null;
				this.selectedOptions = [];
				this.virtualSelection = {};
				this.quantity = 1;
				this.isCustom = false;
				this.customOptionIndex = -1;
				this.customValue = null;

				this.querySelector("[data-edit-back]").addEventListener("click", () => this.close());
				this.querySelector("[data-qty-minus]").addEventListener("click", () => {
					this.quantity = Math.max(1, this.quantity - 1);
					this.qtyValue.textContent = this.quantity;
					this.updatePrice();
				});
				this.querySelector("[data-qty-plus]").addEventListener("click", () => {
					this.quantity = this.quantity + 1;
					this.qtyValue.textContent = this.quantity;
					this.updatePrice();
				});
				this.saveButton.addEventListener("click", () => this.save());
			}

			get drawerInner() {
				return this.closest(".drawer__inner");
			}

			get virtualOptions() {
				return (this.data && this.data.bcpo && this.data.bcpo.virtual_options) || [];
			}

			open() {
				const script = document.querySelector(
					`script[data-cart-item-data][data-key="${this.dataset.key}"]`
				);
				if (!script) return;
				try {
					this.data = JSON.parse(script.textContent);
				} catch (e) {
					return;
				}
				this.initState();
				this.render();
				this.isModal =
					!this.closest(".drawer__inner") ||
					window.matchMedia("(min-width: 990px)").matches;
				if (this.isModal) {
					// PC 端：移到 body 以居中弹窗展示（盖在抽屉遮罩之上）
					this._home = { parent: this.parentNode, next: this.nextSibling };
					this.classList.add("is-modal");
					document.body.appendChild(this);
				} else {
					this.drawerInner.classList.add("is-editing");
				}
				this.hidden = false;
				this.querySelector(".cart-edit__scroll").scrollTop = 0;
			}

			close() {
				this.hidden = true;
				if (this.drawerInner) this.drawerInner.classList.remove("is-editing");
				this.classList.remove("is-modal");
				const home = this._home;
				this._home = null;
				if (home && home.parent && home.parent.isConnected) {
					// 放回抽屉原位置
					home.parent.insertBefore(
						this,
						home.next && home.next.parentNode === home.parent ? home.next : null
					);
				} else if (this.isConnected) {
					// 抽屉已重渲染，原位置不存在，移除游离副本（新副本已在 drawer DOM 中）
					this.remove();
				}
			}

			initState() {
				const names = this.data.option_names || [];
				this.selectedOptions = names.map((name, i) => {
					const found = (this.data.options || []).find((o) => o.name === name);
					if (found) return found.value;
					const first = (this.data.variants || [])[0];
					return first ? first.options[i] : null;
				});

				// 收集 conditional_value，找出包含它的 option（Standard/Custom 模式）
				this.customOptionIndex = -1;
				this.customValue = null;
				this.virtualOptions.forEach((vo) => {
					if (vo.conditional_value) {
						names.forEach((name, i) => {
							const values = this.getAllOptionValues(i);
							if (values.includes(vo.conditional_value)) {
								this.customOptionIndex = i;
								this.customValue = vo.conditional_value;
							}
						});
					}
				});
				this.isCustom =
					this.customOptionIndex >= 0 &&
					this.selectedOptions[this.customOptionIndex] === this.customValue;

				// 虚拟选项当前值：properties -> preselect
				this.virtualSelection = {};
				this.virtualOptions.forEach((vo) => {
					const current = this.getVirtualCurrentValue(vo);
					const selected = current || vo.preselect_value;
					if (!selected) return;
					const value = this.findVirtualValue(vo, selected);
					if (value) this.virtualSelection[vo.title] = { key: value.key, price: value.price };
				});

				this.quantity = this.data.quantity || 1;
				this.qtyValue.textContent = this.quantity;
			}

			getAllOptionValues(index) {
				const seen = [];
				(this.data.variants || []).forEach((v) => {
					const value = v.options[index];
					if (value != null && !seen.includes(value)) seen.push(value);
				});
				return seen;
			}

			getOptionValues(index) {
				const variants = this.data.variants || [];
				const all = this.getAllOptionValues(index);
				if (!variants.length) return all;
				const selected = this.selectedOptions || [];

				// 上下文过滤：其他选项按当前选中值匹配时，该值存在可售变体
				const contextual = all.filter((value) =>
					variants.some(
						(vv) =>
							vv.available &&
							vv.options[index] === value &&
							vv.options.every(
								(o, j) => j === index || !selected[j] || o === selected[j]
							)
					)
				);
				// 全局兜底：任意可售变体包含该值
				const global = all.filter((value) =>
					variants.some((vv) => vv.available && vv.options[index] === value)
				);
				let result = contextual.length ? contextual : global;
				if (!result.length) result = all;

				// 当前选中值即使不可售也保留显示，避免选中态丢失
				const current = selected[index];
				if (current != null && !result.includes(current)) {
					result = [...result, current];
				}
				return result;
			}

			getOptionType(index) {
				const shopifyOptions =
					(this.data.bcpo && this.data.bcpo.shopify_options) || [];
				const config = shopifyOptions[index];
				return config ? config.type : "default";
			}

			isVirtualOptionVisible(vo) {
				if (!vo.conditional_option) return true;
				const ref = String(vo.conditional_option);
				const names = this.data.option_names || [];
				const shopifyOptions =
					(this.data.bcpo && this.data.bcpo.shopify_options) || [];
				// 优先按 shopify_options 的 unique 匹配（如 unique="1"）
				let index = shopifyOptions.findIndex((s) => String(s.unique) === ref);
				if (index === -1) index = names.indexOf(ref);
				if (index === -1 && !isNaN(parseInt(ref))) {
					// 数字按 1-based option 序号兜底解析
					const num = parseInt(ref);
					if (num >= 1 && num <= names.length) index = num - 1;
				}
				if (index === -1) return true;
				return this.selectedOptions[index] === vo.conditional_value;
			}

			findVirtualValue(vo, key) {
				const values = vo.values || [];
				const exact = values.find((v) => String(v.key) === String(key));
				if (exact) return exact;
				const num = Number(key);
				if (isNaN(num) || !values.length) return { key, price: 0 };
				// 无精确匹配时取数值最接近的项，保证价格不回退为 0
				let best = values[0];
				let bestDiff = Math.abs(Number(values[0].key) - num);
				values.forEach((v) => {
					const diff = Math.abs(Number(v.key) - num);
					if (diff < bestDiff) {
						bestDiff = diff;
						best = v;
					}
				});
				return best;
			}

			getVirtualCurrentValue(vo) {
				const raw = this.data.properties ? this.data.properties[vo.title] : null;
				if (!raw) return null;
				return String(raw).split(" | ")[0];
			}

			render() {
				const names = this.data.option_names || [];
				let html = "";

				if (!this.data.has_only_default_variant) {
					names.forEach((name, i) => {
					const values = this.getOptionValues(i);
					if (!values.length) return;
					const current =
						this.isCustom && i === this.customOptionIndex
							? this.customValue
							: this.selectedOptions[i];

					if (i === this.customOptionIndex) {
						html += this.renderCustomOptionGroup(name, i, values);
					} else {
						html += this.renderOptionGroup(name, i, values, current);
					}

					// 该 option 选中 custom 值时，渲染关联的 conditional 虚拟选项
					if (this.isCustom && i === this.customOptionIndex) {
						html += this.renderVirtualOptions();
					}
					});
				}

				// 无条件虚拟选项：始终渲染一次（没有 custom option 或 Standard 模式时）
				const hasUnconditional = this.virtualOptions.some(
					(vo) => !vo.conditional_option
				);
				if (hasUnconditional && !(this.isCustom && this.customOptionIndex >= 0)) {
					html += this.renderVirtualOptions();
				}

				this.optionsContainer.innerHTML = html;
				this.bindOptionEvents();
				this.updatePrice();
			}

			renderCustomOptionGroup(name, index, values) {
				const standardValues = values.filter((v) => v !== this.customValue);
				const label = `${escapeHtml(name)}: ${escapeHtml(this.selectedOptions[index] || "")}`;
				const pickTip = (value) =>
					Array.isArray(value) ? value[0] : value;
				const tipsText =
					pickTip(this.data.product_tips) || pickTip(this.data.size_tip) || "";
				const tips = tipsText
					? `<p class="cart-edit__tips">${escapeHtml(tipsText)}</p>`
					: "";
				let html = `<div class="cart-edit__option-group" data-option-group="${index}">
					<span class="cart-edit__label">${label}</span>
					<div class="cart-edit__tabs">
						<button type="button" class="cart-edit__tab ${!this.isCustom ? "is-selected" : ""}" data-tab="standard">${escapeHtml(this.dataset.textStandard)}</button>
						<button type="button" class="cart-edit__tab ${this.isCustom ? "is-selected" : ""}" data-tab="custom">${escapeHtml(this.dataset.textCustom)}</button>
					</div>
					${tips}`;
				if (!this.isCustom) {
					html += this.renderValueButtons(index, standardValues, this.selectedOptions[index]);
				}
				html += `</div>`;
				return html;
			}

			renderOptionGroup(name, index, values, current) {
				const type = this.getOptionType(index);
				const label = `${escapeHtml(name)}: ${escapeHtml(current || "")}`;
				let control;
				if (type === "auto-images") {
					control = this.renderSwatches(index, values, current);
				} else if (values.length > 8) {
					control = this.renderSelect(index, values, current);
				} else {
					control = this.renderValueButtons(index, values, current);
				}
				return `<div class="cart-edit__option-group" data-option-group="${index}">
					<span class="cart-edit__label">${label}</span>
					${control}
				</div>`;
			}

			renderSwatches(index, values, current) {
				const names = this.data.option_names || [];
				const isColorAutoImages =
					/color/i.test(names[index] || "") &&
					this.getOptionType(index) === "auto-images";
				const variantPictures = this.data.variant_picture || [];
				const buttons = values
					.map((value) => {
						let image = null;
						if (isColorAutoImages && variantPictures.length) {
							// custom.variant_picture 中 alt 与选项值匹配的图片优先（同产品页逻辑）
							const match = variantPictures.find(
								(pic) =>
									pic.alt &&
									pic.src &&
									pic.alt.trim() === String(value).trim()
							);
							if (match) image = match.src;
						}
						if (!image) {
							const variant = (this.data.variants || []).find(
								(v) => v.options[index] === value && v.image
							);
							if (variant) image = variant.image;
						}
						const style = image ? ` style="background-image: url('${image}')"` : "";
						const selected = value === current ? " is-selected" : "";
						return `<button type="button" class="cart-edit__swatch${selected}" data-option-index="${index}" data-option-value="${escapeHtml(value)}"${style} aria-label="${escapeHtml(value)}"></button>`;
					})
					.join("");
				return `<div class="cart-edit__swatches">${buttons}</div>`;
			}

			renderSelect(index, values, current) {
				const options = values
					.map(
						(value) =>
							`<option value="${escapeHtml(value)}" ${value === current ? "selected" : ""}>${escapeHtml(value)}</option>`
					)
					.join("");
				return `<select class="cart-edit__select" data-option-index="${index}">${options}</select>`;
			}

			renderValueButtons(index, values, current) {
				const buttons = values
					.map((value) => {
						const selected = value === current ? " is-selected" : "";
						return `<button type="button" class="cart-edit__value-btn${selected}" data-option-index="${index}" data-option-value="${escapeHtml(value)}">${escapeHtml(value)}</button>`;
					})
					.join("");
				return `<div class="cart-edit__values">${buttons}</div>`;
			}

			renderVirtualOptions() {
				const visible = this.virtualOptions.filter((vo) =>
					this.isVirtualOptionVisible(vo)
				);
				if (!visible.length) return "";

				const renderNumberInput = (vo) => {
					const selected = this.virtualSelection[vo.title];
					const current = selected ? selected.key : null;
					const nums = (vo.values || [])
						.map((v) => Number(v.key))
						.filter((n) => !isNaN(n));
					const min = nums.length ? Math.min(...nums) : null;
					const max = nums.length ? Math.max(...nums) : null;
					return `<input
						type="number"
						class="cart-edit__number"
						data-virtual-number="${escapeHtml(vo.title)}"
						value="${escapeHtml(current != null ? current : "")}"
						${min !== null ? `min="${min}"` : ""}
						${max !== null ? `max="${max}"` : ""}
						step="1"
						inputmode="numeric"
					>`;
				};

				// 条件字段（conditional_value，如 Width/Length）：同行展示
				const conditionalVos = visible.filter((vo) => vo.conditional_value);
				const otherVos = visible.filter((vo) => !vo.conditional_value);

				let html = "";
				if (conditionalVos.length) {
					html += `<div class="cart-edit__option-row">`;
					html += conditionalVos
						.map(
							(vo) => `<div class="cart-edit__option-item">
								<span class="cart-edit__label">${escapeHtml(vo.title)}</span>
								${renderNumberInput(vo)}
							</div>`
						)
						.join("");
					html += `</div>`;
				}

				html += otherVos
					.map((vo) => {
						const selected = this.virtualSelection[vo.title];
						const current = selected ? selected.key : null;
						let control;
						if ((vo.values || []).length > 8) {
							const options = vo.values
								.map(
									(v) =>
										`<option value="${escapeHtml(v.key)}" ${v.key === current ? "selected" : ""}>${escapeHtml(v.key)}</option>`
								)
								.join("");
							control = `<select class="cart-edit__select" data-virtual-option="${escapeHtml(vo.title)}">${options}</select>`;
						} else {
							const buttons = vo.values
								.map((v) => {
									const isSel = v.key === current ? " is-selected" : "";
									return `<button type="button" class="cart-edit__value-btn${isSel}" data-virtual-option="${escapeHtml(vo.title)}" data-virtual-value="${escapeHtml(v.key)}">${escapeHtml(v.key)}</button>`;
								})
								.join("");
							control = `<div class="cart-edit__values">${buttons}</div>`;
						}
						return `<div class="cart-edit__option-group cart-edit__option-group--virtual">
							<span class="cart-edit__label">${escapeHtml(vo.title)}</span>
							${control}
						</div>`;
					})
					.join("");
				return html;
			}

			bindOptionEvents() {
				this.optionsContainer.querySelectorAll("[data-tab]").forEach((tab) => {
					tab.addEventListener("click", () => {
						this.isCustom = tab.dataset.tab === "custom";
						if (this.customOptionIndex >= 0) {
							this.selectedOptions[this.customOptionIndex] = this.isCustom
								? this.customValue
								: this.getOptionValues(this.customOptionIndex).find(
										(v) => v !== this.customValue
									);
						}
						this.render();
					});
				});

				this.optionsContainer
					.querySelectorAll("[data-option-value]")
					.forEach((button) => {
						button.addEventListener("click", () => {
							this.selectedOptions[parseInt(button.dataset.optionIndex)] =
								button.dataset.optionValue;
							this.render();
						});
					});

				this.optionsContainer
					.querySelectorAll("select[data-option-index]")
					.forEach((select) => {
						select.addEventListener("change", (event) => {
							event.stopPropagation();
							this.selectedOptions[parseInt(select.dataset.optionIndex)] =
								select.value;
							this.updatePrice();
						});
					});

				this.optionsContainer
					.querySelectorAll("[data-virtual-value]")
					.forEach((button) => {
						button.addEventListener("click", () => {
							const vo = this.virtualOptions.find(
								(v) => v.title === button.dataset.virtualOption
							);
							const value = vo.values.find(
								(v) => v.key === button.dataset.virtualValue
							);
							this.virtualSelection[vo.title] = {
								key: value.key,
								price: value.price,
							};
							this.render();
						});
					});

				this.optionsContainer
					.querySelectorAll("select[data-virtual-option]")
					.forEach((select) => {
						select.addEventListener("change", (event) => {
							event.stopPropagation();
							const vo = this.virtualOptions.find(
								(v) => v.title === select.dataset.virtualOption
							);
							const value = vo.values.find((v) => v.key === select.value);
							this.virtualSelection[vo.title] = {
								key: value.key,
								price: value.price,
							};
							this.updatePrice();
						});
					});

				this.optionsContainer
					.querySelectorAll("[data-virtual-number]")
					.forEach((input) => {
						input.addEventListener("change", (event) => {
							event.stopPropagation();
							const vo = this.virtualOptions.find(
								(v) => v.title === input.dataset.virtualNumber
							);
							if (!vo) return;
							const nums = (vo.values || [])
								.map((v) => Number(v.key))
								.filter((n) => !isNaN(n));
							let num = parseFloat(input.value);
							if (isNaN(num)) {
								const fallback = this.virtualSelection[vo.title];
								num = fallback ? Number(fallback.key) : NaN;
							}
							if (!isNaN(num)) {
								if (nums.length) {
									num = Math.min(Math.max(num, Math.min(...nums)), Math.max(...nums));
								}
								input.value = num;
							}
							const match = this.findVirtualValue(vo, input.value);
							this.virtualSelection[vo.title] = {
								key: match.key,
								price: match.price,
							};
							this.updatePrice();
						});
					});
			}

			getSelectedVariant() {
				return (this.data.variants || []).find((v) =>
					v.options.every((option, i) => option === this.selectedOptions[i])
				);
			}

			getAddonsPrice() {
				return this.virtualOptions.reduce((sum, vo) => {
					if (!this.isVirtualOptionVisible(vo)) return sum;
					const selected = this.virtualSelection[vo.title];
					return sum + (selected ? Number(selected.price) || 0 : 0);
				}, 0);
			}

			updatePrice() {
				const variant = this.getSelectedVariant();
				if (!variant) {
					this.saveButton.disabled = true;
					this.saveLabel.textContent = this.dataset.textSaveChanges;
					return;
				}
				this.saveButton.disabled = false;
				const unit = variant.price + this.getAddonsPrice() * 100;
				// formatMoney 输出可能含 <span class="money">（shop.money_format 配置），用 innerHTML 渲染
				this.saveLabel.innerHTML = `${escapeHtml(this.dataset.textSaveChanges)} · ${formatMoney(unit * this.quantity)}`;
				this.updateProductImage(variant);
			}

			updateProductImage(variant) {
				const image = this.querySelector("[data-edit-image]");
				if (!image || !variant || !variant.image) return;
				if (image.getAttribute("src") !== variant.image) {
					image.setAttribute("src", variant.image);
				}
			}

			buildProperties() {
				const props = {};
				Object.keys(this.data.properties || {}).forEach((key) => {
					// 跳过历史污染产生的纯数字键（旧版脏数据回传留下的）
					if (/^\d+$/.test(key)) return;
					props[key] = this.data.properties[key];
				});
				this.virtualOptions.forEach((vo) => {
					if (!this.isVirtualOptionVisible(vo)) {
						delete props[vo.title];
						return;
					}
					const selected = this.virtualSelection[vo.title];
					if (!selected) {
						delete props[vo.title];
						return;
					}
					const price = Number(selected.price) || 0;
					props[vo.title] =
						price !== 0 ? `${selected.key} | $${price}` : selected.key;
				});
				return props;
			}

			save() {
				const variant = this.getSelectedVariant();
				if (!variant) return;
				this.saveButton.disabled = true;

				const properties = this.buildProperties();
				const sections = ["cart-drawer", "cart-icon-bubble"];
				const cartItemsEl = document.getElementById("main-cart-items");
				if (cartItemsEl) {
					sections.push(cartItemsEl.dataset.id || "main-cart-items");
				}
				const cartFooterEl = document.getElementById("main-cart-footer");
				if (cartFooterEl) {
					sections.push(cartFooterEl.dataset.id || "main-cart-footer");
				}

				const handleSuccess = (parsedState) => {
					this.close();
					renderCartDrawerSections(parsedState);
					// 行数据随每次局部刷新重建；全局任一行属于配件系列即视为有配件
					// （同时覆盖侧边购物车与 /cart 页的行数据）
					const hasAccessories = !!document.querySelector(
						'script[data-cart-item-data][data-in-acc-collection="true"]'
					);
					showCartToast(
						hasAccessories
							? this.dataset.textChanged
							: this.dataset.textChangedShort
					);
				};
				const handleFailure = () => {
					this.saveButton.disabled = false;
					const toast = this.querySelector("[data-edit-toast]");
					if (toast) {
						clearTimeout(this._toastTimer);
						toast.textContent = this.dataset.textFailed;
						toast.classList.add("is-visible");
						this._toastTimer = setTimeout(() => {
							toast.classList.remove("is-visible");
						}, 3000);
					} else {
						showCartToast(this.dataset.textFailed);
					}
				};

				if (variant.id === this.data.variant_id) {
					const body = JSON.stringify({
						line: this.data.line,
						quantity: this.quantity,
						properties,
						sections,
					});
					fetch(routes.cart_change_url, { ...fetchConfig(), body })
						.then((response) => (response.ok ? response.json() : Promise.reject()))
						.then(handleSuccess)
						.catch(handleFailure);
					return;
				}

				// 变体变更：先移除旧行，再新增新配置（顺序避免把新增的行误删/顶到前面）
				const removeBody = JSON.stringify({
					line: this.data.line,
					quantity: 0,
					sections,
				});
				fetch(routes.cart_change_url, { ...fetchConfig(), body: removeBody })
					.then((response) => (response.ok ? response.json() : Promise.reject()))
					.then(() => {
						const addBody = JSON.stringify({
							items: [
								{
									id: variant.id,
									quantity: this.quantity,
									properties,
								},
							],
							sections,
						});
						return fetch(routes.cart_add_url, {
							...fetchConfig(),
							body: addBody,
						}).then((response) =>
							response.ok ? response.json() : Promise.reject()
						);
					})
					.then((addState) => {
						if (addState.errors) return Promise.reject();
						handleSuccess(addState);
					})
					.catch(handleFailure);
			}
		}
	);
}

// Edit 入口（document 级委托，同时覆盖侧边购物车与 /cart 页；行重渲染后依然有效）
// 部分加购流程只局部替换内容，新增的 cart-item-edit 面板可能缺失；缺失时拉取最新 section HTML 重建面板。
function rebuildCartItemEditPanels() {
	const jobs = [
		{
			section: "cart-drawer",
			container: () => document.querySelector("#CartDrawer .drawer__inner"),
		},
	];
	const cartItemsEl = document.getElementById("main-cart-items");
	if (cartItemsEl) {
		jobs.push({
			section: cartItemsEl.dataset.id || "main-cart-items",
			container: () => document.querySelector("cart-items"),
		});
	}
	return Promise.all(
		jobs.map((job) =>
			fetch(`${routes.cart_url}?section_id=${job.section}`)
				.then((response) => response.text())
				.then((responseText) => {
					const doc = new DOMParser().parseFromString(responseText, "text/html");
					const container = job.container();
					if (!container) return;
					container.querySelectorAll("cart-item-edit").forEach((el) => el.remove());
					doc.querySelectorAll("cart-item-edit").forEach((el) => {
						container.appendChild(el);
					});
				})
		)
	).then(() => {});
}

document.addEventListener("click", (event) => {
	const button = event.target.closest("[data-edit-item]");
	if (!button) return;
	let panel = document.querySelector(
		`cart-item-edit[data-key="${button.dataset.key}"]`
	);
	if (panel) {
		panel.open();
		return;
	}
	rebuildCartItemEditPanels()
		.catch(() => {})
		.then(() => {
			panel = document.querySelector(
				`cart-item-edit[data-key="${button.dataset.key}"]`
			);
			if (panel) panel.open();
		});
});

function updateAccessoryCompatibility() {
	const containers = [];
	const drawerInner = document.querySelector("#CartDrawer .drawer__inner");
	if (drawerInner) containers.push(drawerInner);
	const cartItemsEl = document.getElementById("main-cart-items");
	if (cartItemsEl) containers.push(cartItemsEl);
	containers.forEach(updateContainerCompatibility);
}

function updateContainerCompatibility(container) {
	if (!container) return;
	const root =
		container.id === "main-cart-items"
			? container
			: document.getElementById("CartDrawer");

	const items = [];
	container.querySelectorAll("script[data-cart-item-data]").forEach((script) => {
		try {
			items.push(JSON.parse(script.textContent));
		} catch (e) {
			/* 忽略解析失败的行 */
		}
	});
	if (!items.length) return;

	const extractMaxNumber = (item) => {
		let max = 0;
		const scan = (str) => {
			const nums = String(str).match(/\d+\.?\d*/g);
			if (!nums) return;
			nums.forEach((n) => {
				const value = parseFloat(n);
				if (value > max) max = value;
			});
		};
		(item.options || []).forEach((o) => scan(o.value));
		Object.keys(item.properties || {}).forEach((key) => {
			if (/^\d+$/.test(key) || key.charAt(0) === "_") return;
			scan(String(item.properties[key]).split(" | ")[0]);
		});
		return max;
	};

	const accessoryItems = items.filter((item) => item.option_range);
	const findRow = (line) =>
		container.querySelector(`[data-cart-line="${line}"]`);
	const hidePrompt = (item) => {
		const row = findRow(item.line);
		const prompt = row && row.querySelector("[data-accessory-prompt]");
		if (prompt) prompt.hidden = true;
	};

	// 主产品候选：无 option_range、非配件（product_type 含 Accessories 的行排除）、
	// 且产品在固定元对象条目（data-accessory-main-ids）的 product 列表里；
	// 一个主产品最多被 2 个配件匹配；定制产品的尺寸从 VO properties（Width/Length 等）提取
	const allowedIds = ((root && root.dataset.accessoryMainIds) || "")
		.split(",")
		.filter(Boolean);
	if (!allowedIds.length) {
		// 元对象条目不存在或未配置 product：无可判断依据，一律不提示
		accessoryItems.forEach(hidePrompt);
		return;
	}
	const mains = items
		.filter(
			(item) =>
				!item.option_range &&
				!/accessories/i.test(item.product_type || "") &&
				allowedIds.includes(String(item.product_id))
		)
		.map((item) => ({
			line: item.line,
			num: extractMaxNumber(item),
			used: 0,
		}));

	// 购物车中没有主产品时无可比对象，一律不提示
	if (!mains.length || !accessoryItems.length) {
		accessoryItems.forEach(hidePrompt);
		return;
	}

	accessoryItems.forEach((item) => {
		const parts = String(item.option_range).split("-").map(Number);
		const min = parts[0];
		const max = parts[1];
		let matched = null;
		for (const main of mains) {
			// 尺寸未知（提取不到数字）时不做数值判断，视为适配
			const inRange = main.num <= 0 || (main.num >= min && main.num <= max);
			if (inRange && main.used < 2) {
				matched = main;
				break;
			}
		}
		if (matched) {
			matched.used += 1;
			hidePrompt(item);
		} else {
			const row = findRow(item.line);
			const prompt = row && row.querySelector("[data-accessory-prompt]");
			if (prompt) prompt.hidden = false;
		}
	});
}

const observeCompatibilityRoot = (el) => {
	if (!el) return;
	// 覆盖所有重渲染路径（数量更新、编辑保存、加购等）
	new MutationObserver(() => updateAccessoryCompatibility()).observe(el, {
		childList: true,
		subtree: true,
	});
};
observeCompatibilityRoot(document.querySelector("#CartDrawer .drawer__inner"));
observeCompatibilityRoot(document.getElementById("main-cart-items"));
updateAccessoryCompatibility();
